import { FC, useState, useEffect, useRef } from 'react'
import { Select } from 'antd'
import {
  searchPoolUsers,
  listProfiles,
  PoolUserSearchItem,
  PoolProfileListItem,
} from '@/api/poolAdminApi'

export interface UserPickerProps {
  value?: string
  onChange?: (userId: string | undefined) => void
  placeholder?: string
  style?: React.CSSProperties
  allowClear?: boolean
  width?: number | string
  activityId?: string
  /** 选中值未命中 options 时(如初始 admin 自身)闭合态的显示文本;缺省回退显示原始 ID */
  valueLabel?: string
}

const shortId = (id: string) => (id && id.startsWith('seed_') ? id.slice(0, 16) + '…' : (id || '').slice(0, 8))

function profileToOption(p: PoolProfileListItem): PoolUserSearchItem {
  return {
    user_id: p.user_id,
    nickname: p.nickname || '',
    gender: p.gender,
    age: p.age,
    city: p.city,
    occupation: p.occupation,
    profile_status: p.status,
  }
}

const PRESET_PAGE_SIZE = 20

export const UserPicker: FC<UserPickerProps> = ({
  value,
  onChange,
  placeholder = '搜索昵称 / ID',
  style,
  allowClear = true,
  width = 220,
  activityId,
  valueLabel,
}) => {
  const [options, setOptions] = useState<PoolUserSearchItem[]>([])
  const [searching, setSearching] = useState(false)
  const [searchText, setSearchText] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout>>()

  const [presetItems, setPresetItems] = useState<PoolUserSearchItem[]>([])
  const [presetTotal, setPresetTotal] = useState(0)
  const [presetOffset, setPresetOffset] = useState(0)
  const [presetLoading, setPresetLoading] = useState(false)
  const [presetLoaded, setPresetLoaded] = useState(false)

  useEffect(() => {
    if (!value) setOptions([])
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = undefined
    }
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [value])

  useEffect(() => {
    if (!activityId) {
      setPresetItems([])
      setPresetLoaded(false)
      setPresetTotal(0)
      setPresetOffset(0)
      return
    }
    setPresetItems([])
    setPresetLoaded(false)
    setPresetTotal(0)
    setPresetOffset(0)
  }, [activityId])

  const runSearch = (q: string) => {
    setSearching(true)
    searchPoolUsers(q)
      .then(r => setOptions(r.users || []))
      .catch(() => setOptions([]))
      .finally(() => setSearching(false))
  }

  const onSearch = (v: string) => {
    setSearchText(v)
    if (!v && activityId) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => runSearch(v), 350)
  }

  const onSelect = (v: string) => {
    onChange?.(v)
  }

  const onClear = () => {
    setSearchText('')
    setOptions([])
    onChange?.(undefined)
  }

  const loadPreset = async (append: boolean) => {
    if (!activityId || presetLoading) return
    setPresetLoading(true)
    try {
      const offset = append ? presetOffset : 0
      const r = await listProfiles({
        activity_id: activityId,
        limit: PRESET_PAGE_SIZE,
        offset,
      })
      const items = (r.profiles || []).map(profileToOption)
      setPresetItems(prev => (append ? [...prev, ...items] : items))
      setPresetTotal(r.total || 0)
      setPresetOffset(offset + items.length)
      setPresetLoaded(true)
    } catch {
      if (!append) setPresetItems([])
    } finally {
      setPresetLoading(false)
    }
  }

  const onDropdownVisibleChange = (open: boolean) => {
    if (open && activityId && !presetLoaded && !searchText) {
      loadPreset(false)
    }
  }

  const onPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!activityId || searchText || presetLoading) return
    if (presetLoaded && presetOffset >= presetTotal) return
    const t = e.currentTarget
    if (t.scrollHeight - t.scrollTop - t.clientHeight < 60) {
      loadPreset(true)
    }
  }

  const activeOptions = searchText
    ? options
    : activityId
    ? presetItems
    : options

  // value 未命中 options 时(初始 admin 自身/切 run-as 后未搜索)注入合成项,保证闭合态显示昵称而非 ID
  const valueMissed = !!value && !activeOptions.some(o => o.user_id === value)
  const selectOptions = activeOptions.map(o => ({
    value: o.user_id,
    label: (
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
        <span style={{ fontSize: 13 }}>
          {o.nickname || <span style={{ color: '#999' }}>无昵称</span>}
          {o.gender && <span style={{ color: '#999', marginLeft: 6 }}>{o.gender}</span>}
          {o.age != null && <span style={{ color: '#999', marginLeft: 4 }}>{o.age}岁</span>}
        </span>
        <span style={{ fontSize: 11, color: '#999' }}>
          {shortId(o.user_id)}
          {o.city ? ` · ${o.city}` : ''}
        </span>
      </div>
    ),
  }))
  if (valueMissed && valueLabel) {
    selectOptions.push({
      value: value!,
      label: (
        <span style={{ fontSize: 13 }}>
          {valueLabel}
          <span style={{ fontSize: 11, color: '#999', marginLeft: 6 }}>{shortId(value!)}</span>
        </span>
      ),
    })
  }

  const showFooter =
    !!activityId && !searchText && presetLoaded && presetItems.length > 0
  const footerNode = showFooter ? (
    <div
      style={{
        textAlign: 'center',
        color: '#999',
        fontSize: 11,
        padding: '4px 0',
      }}
    >
      {presetOffset >= presetTotal
        ? `共 ${presetTotal} 人,已全部加载`
        : `已加载 ${presetItems.length} / 共 ${presetTotal} 人`}
    </div>
  ) : null

  const notFound = searching
    ? '搜索中...'
    : activityId && !searchText
    ? presetLoading
      ? '加载中...'
      : presetLoaded
      ? presetItems.length === 0
        ? '该活动暂无成员'
        : ''
      : '下拉查看成员列表'
    : '输入昵称或 ID 搜索'

  return (
    <Select
      showSearch
      allowClear={allowClear}
      style={{ width, ...style }}
      value={value}
      placeholder={placeholder}
      filterOption={false}
      onSearch={onSearch}
      searchValue={searchText}
      onClear={onClear}
      onChange={onSelect}
      onDropdownVisibleChange={onDropdownVisibleChange}
      onPopupScroll={onPopupScroll}
      loading={searching || presetLoading}
      notFoundContent={notFound}
      options={selectOptions}
      dropdownRender={
        showFooter
          ? menu => (
              <div>
                {menu}
                {footerNode}
              </div>
            )
          : undefined
      }
    />
  )
}